import { connectColors } from '@/theme/colors';
import type { EnvironmentLabel } from '@/utils/get-environment';
import { Button, InlineAlert, Modal } from '@lego/connect-components-react';
import { Flex, Text, VerticalStack } from '@lego/klik-ui';
import { StatusCircleExclamation } from '@lego/klik-ui-icons';
import type { RefObject } from 'react';
import { getSettingChangeConfirmation } from '../dev-dashboard-helpers';

type Props = {
  isOpen: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  environment: EnvironmentLabel;
  settingKey: string;
  newValue: string;
  isSaving: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

/**
 * Asks the operator to confirm a setting change, and names the environment. Production shows an extra
 * warning. Same layout as the reset novelty prices confirmation.
 */
export const SettingChangeConfirmModal = ({
  isOpen,
  triggerRef,
  environment,
  settingKey,
  newValue,
  isSaving,
  onConfirm,
  onClose,
}: Props) => {
  const confirmation = getSettingChangeConfirmation(environment);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="small"
      triggerRef={triggerRef}
    >
      <Modal.Title>Change setting</Modal.Title>
      <Modal.Content>
        <VerticalStack alignItems="stretch" gap="12px">
          <Flex gap="8px">
            <StatusCircleExclamation
              color={connectColors.core.red[900]}
              width="24px"
              height="24px"
            />
            <VerticalStack alignItems="start" gap="4px">
              <Text>
                <b>{confirmation.question}</b>
              </Text>
              <Text>
                Set <code>{settingKey}</code> to <code>{newValue}</code>.
              </Text>
            </VerticalStack>
          </Flex>
          {confirmation.productionWarning && (
            <InlineAlert showIcon variant="negative">
              <InlineAlert.Title>Production</InlineAlert.Title>
              <InlineAlert.Content>
                {confirmation.productionWarning}
              </InlineAlert.Content>
            </InlineAlert>
          )}
        </VerticalStack>
      </Modal.Content>
      <Modal.Footer>
        <Button
          aria-label="No"
          disabled={isSaving}
          appearance="ghost"
          variant="neutral"
          onClick={onClose}
        >
          No
        </Button>
        <Button
          aria-label="Yes"
          disabled={isSaving}
          loading={isSaving}
          variant="negative"
          onClick={onConfirm}
        >
          Yes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
